import { CheckCircle, X } from 'lucide-react';
import { Button } from './Button';
import { ReactNode } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message?: string;
  children?: ReactNode;
  variant?: 'success' | 'default';
  actionLabel?: string;
  onAction?: () => void;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Modal({
  isOpen,
  onClose,
  title,
  message,
  children,
  variant = 'default',
  actionLabel = 'OK',
  onAction,
  showCloseButton = true,
  size = 'md',
}: ModalProps) {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else {
      onClose();
    }
  };

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in p-4"
      onClick={handleBackdropClick}
    >
      <div className={`relative w-full ${sizeClasses[size]} bg-white rounded-2xl shadow-2xl transform transition-all animate-scale-in max-h-[90vh] flex flex-col`}>
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="p-8 overflow-y-auto">
          {children ? (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pr-8">{title}</h2>
              {children}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center space-y-4">
              {variant === 'success' && (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              )}

              <h2 className="text-2xl font-bold text-gray-900">{title}</h2>

              {message && (
                <p className="text-gray-600 leading-relaxed">{message}</p>
              )}

              <div className="w-full pt-4">
                <Button onClick={handleAction} className="w-full">
                  {actionLabel}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
